import { LotEventTypes, LotStatus } from '@parallel-fluidics/shared/constants';
import { lotEventToFirestore, lotFromFirestore } from '@parallel-fluidics/shared/models/lots';
import {
  collection,
  doc,
  getFirestore,
  increment,
  serverTimestamp,
} from 'firebase/firestore';

/** Thrown by consumers when a re-read shows the BOM is no longer covered. */
export const BOM_NO_LONGER_COVERED = 'The scanned bags no longer cover the BOM — a bag may have been consumed elsewhere. Rescan and retry.';

/**
 * Read fresh copies of the lots about to be consumed, inside a
 * transaction (in input order). Throws if any lot is missing or in a
 * status that can't be consumed (Quarantined / Awaiting inspection).
 *
 * @param {import('firebase/firestore').Transaction} transaction
 * @param {string[]} lotIds
 * @returns {Promise<Array<object>>} Lot models, in `lotIds` order.
 */
export async function readLotsForConsumption(transaction, lotIds) {
  const db = getFirestore();
  const lots = await Promise.all(lotIds.map(async (lotId) => {
    const snap = await transaction.get(doc(db, 'lots', lotId));
    if (!snap.exists()) throw new Error(`Lot ${lotId} no longer exists`);
    return lotFromFirestore(snap);
  }));
  const blocked = lots.find(
    (lot) => [LotStatus.QUARANTINED, LotStatus.AWAITING_INSPECTION].includes(lot.status),
  );
  if (blocked) {
    throw new Error(`${blocked.code} is ${blocked.status} and can't be consumed`);
  }
  return lots;
}

/**
 * Consume from one lot inside a transaction: decrement remainingQty by
 * deltaQty (negative; null means the consumption is untracked, e.g. a
 * bag not on the BOM), derive the status transition (Sealed → Open on
 * first consumption, Empty when the count reaches zero), and write the
 * `consumed` / `status_changed` events.
 *
 * @param {import('firebase/firestore').Transaction} transaction
 * @param {object} params
 * @param {object} params.lot - Fresh lot model from readLotsForConsumption.
 * @param {?number} params.deltaQty - Negative quantity change, or null.
 * @param {string} params.reason - Reason on the `consumed` event.
 * @param {string} params.via - What consumed it (e.g. 'assembly', 'install'),
 *   for the Empty status event's reason.
 * @param {object} params.relatedIds
 * @param {{uid: string, email: string}} params.user
 */
export function consumeLot(transaction, {
  lot, deltaQty, reason, via, relatedIds, user,
}) {
  const lotRef = doc(getFirestore(), 'lots', lot.id);
  const consumedOut = deltaQty != null && lot.remainingQty + deltaQty <= 0;

  let newStatus = null;
  if (consumedOut) newStatus = LotStatus.EMPTY;
  else if (lot.status === LotStatus.SEALED) newStatus = LotStatus.OPEN;

  const patch = {};
  if (deltaQty) patch.remainingQty = increment(deltaQty);
  if (newStatus) {
    patch.status = newStatus;
    patch.statusChangedAt = serverTimestamp();
  }
  if (Object.keys(patch).length > 0) transaction.update(lotRef, patch);

  transaction.set(doc(collection(lotRef, 'events')), lotEventToFirestore({
    type: LotEventTypes.CONSUMED,
    deltaQty,
    reason,
    relatedIds,
    _createdBy: user,
  }, serverTimestamp));
  if (newStatus) {
    transaction.set(doc(collection(lotRef, 'events')), lotEventToFirestore({
      type: LotEventTypes.STATUS_CHANGED,
      reason: newStatus === LotStatus.EMPTY
        ? `${LotStatus.EMPTY} — consumed by ${via}`
        : `${LotStatus.OPEN} — first consumption`,
      relatedIds,
      _createdBy: user,
    }, serverTimestamp));
  }
}
